import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { pretsAPI } from '../../../services/api';
import { Pret, PretValidate, StatutPret } from '../../../types/pret';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';

const HR_COLOR = '#00897B';

interface PretValidationDialogProps {
  open: boolean;
  onClose: () => void;
  pret: Pret | null;
  onSuccess: () => void;
}

const PretValidationDialog: React.FC<PretValidationDialogProps> = ({
  open,
  onClose,
  pret,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [dateDebutPrelevement, setDateDebutPrelevement] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { showSuccess, showError } = useAlert();

  useEffect(() => {
    if (open && pret) {
      setCommentaire('');
      setDateDebutPrelevement(pret.date_debut_prelevement || '');
      setError(null);
    }
  }, [open, pret]);

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DH';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const handleValidate = async (statut: StatutPret) => {
    if (statut === 'VALIDEE' && !dateDebutPrelevement) {
      setError('Veuillez spécifier la date de début de prélèvement pour valider le prêt');
      return;
    }

    setLoading(true);
    try {
      const data: PretValidate = {
        statut,
        commentaire: commentaire || null,
        date_debut_prelevement: statut === 'VALIDEE' ? dateDebutPrelevement : null,
      };
      await pretsAPI.validate(pret!.id, data);
      showSuccess(statut === 'VALIDEE' ? 'Prêt validé avec succès' : 'Prêt refusé');
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!pret) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        Validation de la Demande de Prêt
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loan Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Détails de la demande
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Référence:</Typography>
              <Typography variant="body1" fontWeight="500">{pret.ref_pret || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Date de demande:</Typography>
              <Typography variant="body1">{formatDate(pret.date_demande)}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Employee Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Employé
          </Typography>
          <Typography variant="body1" fontWeight="500">
            {pret.employee?.nom_complet}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pret.employee?.matricule} - {pret.employee?.fonction || 'N/A'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Financial Details */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Montant du prêt:</Typography>
              <Typography variant="h6" sx={{ color: HR_COLOR }}>
                {formatMoney(pret.montant_pret)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Prélèvement mensuel:</Typography>
              <Typography variant="h6">{formatMoney(pret.prelevement_mensuel)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Durée:</Typography>
              <Typography variant="body1" fontWeight="500">{pret.duree_mois} mois</Typography>
            </Grid>
          </Grid>
        </Box>

        {pret.motif && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Motif
            </Typography>
            <Typography variant="body2">{pret.motif}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Validation Form */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Date Début Prélèvement"
              type="date"
              value={dateDebutPrelevement}
              onChange={(e) => {
                setDateDebutPrelevement(e.target.value);
                setError(null);
              }}
              InputLabelProps={{ shrink: true }}
              helperText="Requis pour validation"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Commentaire"
              multiline
              rows={2}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Ajouter un commentaire (optionnel)..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => handleValidate('REFUSEE')}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Refuser'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleValidate('VALIDEE')}
          disabled={loading}
          sx={{ bgcolor: '#66BB6A', '&:hover': { bgcolor: '#4CAF50' } }}
        >
          {loading ? <CircularProgress size={24} /> : 'Valider'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PretValidationDialog;
